import { type ComponentProps } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/util/utils';

type AlertProps = ComponentProps<'div'> & VariantProps<typeof alertVariants>;

/**
 * ui-alert, AL Design system 26:160 (the designer review of 03 Sep 2026, which replaced the
 * older 114:1192 frame): a 1px frame with an 8px corner, 12 / 14 padding, the 16px glyph pinned
 * to the first line and 10 to the text, a 13/18 medium title over a 12/17 muted body, 3 apart.
 * Default sits on the muted ground; destructive, warning and success each have their own
 * surface, border and text colour; info keeps the neutral ground with a muted glyph. Put the
 * icon first, then AlertContent. A destructive or warning alert announces itself, the others
 * are status.
 */
function Alert({ className, variant, role, ref, ...props }: AlertProps) {
    return (
        <div
            data-slot="alert"
            role={role ?? (variant === 'destructive' || variant === 'warning' ? 'alert' : 'status')}
            ref={ref}
            className={cn(alertVariants({ variant }), className)}
            {...props}
        />
    );
}

const alertVariants = cva(
    [
        'bg-muted border-border text-foreground relative flex w-full items-start gap-2.5 rounded-md border px-3.5 py-3 text-sm',
        '[&>svg]:mt-px [&>svg]:size-4 [&>svg]:shrink-0 [&>svg]:text-current',
    ],
    {
        variants: {
            variant: {
                default: '',
                destructive:
                    'bg-destructive-muted border-destructive-muted-border text-destructive [&_[data-slot=alert-description]]:text-destructive-muted-foreground',
                // These three carry more sentence than a form alert, so the title wraps rather
                // than truncating; warning and success tint the whole text block.
                warning:
                    'bg-warning-muted border-warning-muted-border text-warning-muted-foreground [&_[data-slot=alert-description]]:text-warning-muted-foreground [&_[data-slot=alert-title]]:whitespace-normal',
                success:
                    'bg-success-surface border-success-muted-border text-success-muted-foreground [&_[data-slot=alert-description]]:text-success-muted-foreground [&_[data-slot=alert-title]]:whitespace-normal',
                info: 'bg-accent [&>svg]:text-muted-foreground [&_[data-slot=alert-title]]:whitespace-normal',
            },
        },
        defaultVariants: { variant: 'default' },
    },
);

/** Everything after the icon: title over body, 3px apart, taking the remaining width. */
function AlertContent({ className, ref, ...props }: ComponentProps<'div'>) {
    return (
        <div
            data-slot="alert-content"
            ref={ref}
            className={cn('flex min-w-0 flex-1 flex-col gap-[3px]', className)}
            {...props}
        />
    );
}

function AlertTitle({ className, ref, ...props }: ComponentProps<'div'>) {
    return (
        <div
            data-slot="alert-title"
            ref={ref}
            className={cn('truncate text-[13px] leading-[18px] font-medium', className)}
            {...props}
        />
    );
}

function AlertDescription({ className, ref, ...props }: ComponentProps<'div'>) {
    return (
        <div
            data-slot="alert-description"
            ref={ref}
            className={cn('text-muted-foreground text-xs leading-[17px]', className)}
            {...props}
        />
    );
}

/**
 * A trailing action, centred on the alert's row while the icon stays on the first line. Its
 * buttons are the alert's own 24px with the 10px label (READ 204:2116), scoped here rather than
 * a shared button size; a coarse pointer still gets the Button's 44.
 */
function AlertActions({ className, ref, ...props }: ComponentProps<'div'>) {
    return (
        <div
            data-slot="alert-actions"
            ref={ref}
            className={cn(
                'flex shrink-0 items-center gap-2 self-center',
                '[&_[data-slot=button]]:text-label-sm [&_[data-slot=button]]:h-6',
                className,
            )}
            {...props}
        />
    );
}

export { Alert, AlertContent, AlertTitle, AlertDescription, AlertActions, type AlertProps };
